/**
 * Fetches the currently active Okta session for a user.
 * @param {string} oktaUrl - The base URL of the Okta tenant. For example, "https://example.okta.com".
 * @returns {Promise} - A promise that resolves to an object containing the user's login details (`login`) or `undefined` if no session exists.
 *          The promise will be rejected with the HTTP status code if the request fails.
 */
export function getOktaSession(oktaUrl) {
    return fetch(getOktaSessionUrl(oktaUrl), { method: 'GET', credentials: 'include' }).then((response) => {
        if (response.ok) {
            return response.json();
        }
        else {
            return Promise.reject(response.status);
        }
    });
}
/**
 * Logs the user out of their Okta session.
 * @param {string} oktaUrl - The base URL of the Okta tenant. For example, "https://example.okta.com".
 * @returns {Promise} - A promise that resolves to `void` when the logout is successful.
 *          The promise will always resolve to `void`, even if an error occurs during the logout process.
 */
export function logOutOfOkta(oktaUrl) {
    return fetch(getOktaSessionUrl(oktaUrl), { method: 'DELETE', credentials: 'include' })
        .then(() => undefined)
        .catch(() => undefined);
}
/**
 * Constructs the URL for the "Forgot Password" page in Okta.
 * @param {string} oktaUrl - The base URL of the Okta tenant. For example, "https://example.okta.com".
 * @returns {string} A string containing the "Forgot Password" page URL.
 */
export function getOktaForgotPasswordUrl(oktaUrl) {
    if (oktaUrl && !oktaUrl.endsWith('/')) {
        oktaUrl += '/';
    }
    return oktaUrl + 'signin/forgot-password';
}
/**
 * Constructs the URL for the Okta session details API.
 * @param {string} oktaUrl - The base URL of the Okta tenant. For example, "https://example.okta.com".
 * @returns {string} - A string containing the URL for the session details API.
 */
function getOktaSessionUrl(oktaUrl) {
    if (oktaUrl && !oktaUrl.endsWith('/')) {
        oktaUrl += '/';
    }
    return oktaUrl + 'api/v1/sessions/me';
}
